import dayjs from 'dayjs';
import { dequal } from 'dequal';

import { localTime } from '@/utils/utc-time';

import {
  CUSTOM_TIME_RANGE_VALUE,
  DEFAULT_ATTRIBUTES,
  DEFAULT_FILTER,
  DEFAULT_PARTITION_PAIRS,
  DEFAULT_SAMPLE_SIZE,
  DEFAULT_SUMMARY_PROMPT,
  DEFAULT_TIME_RANGE,
  TIME_RANGE_OPTIONS,
} from '../constants';
import {
  Attribute,
  InsightsJobConfig,
  InsightsJobConfigData,
  InsightsJobConfigWithId,
  InsightsJobFormData,
  PartitionPair,
  TimeRange,
} from '../types';

export const formatAttributesForAPI = (
  attributes?: Attribute[]
): InsightsJobConfigData['attribute_schemas'] => {
  // If there are no attributes, or there is only one attribute with no name, description, or type, return undefined
  if (
    !attributes ||
    attributes.length === 0 ||
    (attributes.length === 1 &&
      !(attributes[0].name.trim() && attributes[0].description.trim()))
  ) {
    return undefined;
  }

  return attributes.reduce((acc, attribute) => {
    acc[attribute.name] = {
      type: attribute.type,
      description: attribute.description,
      ...(attribute.type === 'boolean'
        ? { filter_by: !!attribute.filter_by }
        : {}),
    };
    return acc;
  }, {} as Record<string, { type: 'string' | 'number' | 'boolean'; description: string; filter_by?: boolean }>);
};

export const parseAttributesFromAPI = (
  attributes?: InsightsJobConfigData['attribute_schemas']
): Attribute[] => {
  if (!attributes) {
    return [];
  }

  return Object.entries(attributes).map(
    ([name, { type, description, filter_by }]) => ({
      name,
      type,
      description,
      ...(type === 'boolean' ? { filter_by: !!filter_by } : {}),
    })
  );
};

export const formatPartitionPairsForAPI = (
  partitionPairs: PartitionPair[]
): InsightsJobConfigData['partitions'] => {
  // If there are no partition pairs, or there is only one partition pair with no key or value, return undefined
  if (
    partitionPairs.length === 0 ||
    (partitionPairs.length === 1 &&
      !partitionPairs[0].key.trim() &&
      !partitionPairs[0].value.trim())
  ) {
    return undefined;
  }

  const partitionsObj: Record<string, string> = {};
  partitionPairs.forEach((pair) => {
    partitionsObj[pair.key.trim()] = pair.value.trim();
  });
  return partitionsObj;
};

export const parsePartitionPairsFromAPI = (
  partitionPairs?: InsightsJobConfigData['partitions']
): PartitionPair[] => {
  if (!partitionPairs) {
    return [];
  }

  return Object.entries(partitionPairs).map(([key, value]) => ({
    key,
    value,
    id: key,
  }));
};

const timeFormat = 'YYYY-MM-DDTHH:mm';
export const parseTimeRangeFromAPI = ({
  last_n_hours,
  start_time,
  end_time,
}: {
  last_n_hours?: number;
  start_time?: string;
  end_time?: string;
}): TimeRange => {
  if (start_time || end_time) {
    // calculate local time from utc time
    return {
      last_n_hours: CUSTOM_TIME_RANGE_VALUE,
      start_time: start_time
        ? localTime(start_time).format(timeFormat)
        : undefined,
      end_time: end_time ? localTime(end_time).format(timeFormat) : undefined,
    };
  }

  const presetOption = TIME_RANGE_OPTIONS.find(
    (option) => option.value === last_n_hours
  );
  if (
    last_n_hours &&
    presetOption &&
    presetOption.value !== CUSTOM_TIME_RANGE_VALUE
  ) {
    return { last_n_hours };
  } else if (last_n_hours) {
    // Calculate custom time range using the last_n_hours duration
    const now = dayjs();
    const start = now.subtract(last_n_hours, 'hour');
    return {
      last_n_hours: CUSTOM_TIME_RANGE_VALUE,
      start_time: start.format(timeFormat),
      end_time: now.format(timeFormat),
    };
  }

  return DEFAULT_TIME_RANGE;
};

export const convertConfigToFormData = (
  config: InsightsJobConfigData
): InsightsJobFormData => {
  return {
    name: config.name,
    filter: config.filter ? { filter: config.filter } : DEFAULT_FILTER,
    timeRange: parseTimeRangeFromAPI({
      last_n_hours: config.last_n_hours,
      start_time: config.start_time,
      end_time: config.end_time,
    }),
    sample: config.sample ?? DEFAULT_SAMPLE_SIZE,
    partitionPairs: config.partitions
      ? parsePartitionPairsFromAPI(config.partitions)
      : DEFAULT_PARTITION_PAIRS,
    summaryPrompt: config.summary_prompt ?? DEFAULT_SUMMARY_PROMPT,
    attributes: config.attribute_schemas
      ? parseAttributesFromAPI(config.attribute_schemas)
      : DEFAULT_ATTRIBUTES,
  };
};

export const convertFormDataToConfig = (
  formData: InsightsJobFormData
): InsightsJobConfigData => {
  const requestData: InsightsJobConfigData = {
    sample: formData.sample,
    filter: formData.filter.filter,
  };

  // Add name if provided
  if (formData.name && formData.name.trim()) {
    requestData.name = formData.name.trim();
  }

  if (formData.timeRange.last_n_hours !== CUSTOM_TIME_RANGE_VALUE) {
    requestData.last_n_hours = formData.timeRange.last_n_hours;
  } else {
    // Convert local datetime-local values to UTC ISO strings
    if (formData.timeRange.start_time) {
      requestData.start_time = new Date(
        formData.timeRange.start_time
      ).toISOString();
    }
    if (formData.timeRange.end_time) {
      requestData.end_time = new Date(
        formData.timeRange.end_time
      ).toISOString();
    }
  }

  requestData.partitions = formatPartitionPairsForAPI(formData.partitionPairs);

  if (formData.summaryPrompt && formData.summaryPrompt.trim()) {
    requestData.summary_prompt = formData.summaryPrompt;
  }

  requestData.attribute_schemas = formatAttributesForAPI(formData.attributes);

  return requestData;
};

// Prebuilt configs don't have IDs, so we add indices as IDs
export function addIds(
  configs: InsightsJobConfig[]
): InsightsJobConfigWithId[] {
  return configs.map((config, idx) => ({
    ...config,
    id: config.id || idx.toString(),
  }));
}

export const getStartAndEndTime = ({
  lastNHours,
  startTime,
  endTime,
}: {
  lastNHours?: number;
  startTime?: string;
  endTime?: string;
}): { start_time?: string; end_time?: string } => {
  if (lastNHours) {
    const nHoursAgo = new Date(Date.now() - lastNHours * 60 * 60 * 1000);
    const now = new Date();
    return {
      start_time: nHoursAgo.toISOString(),
      end_time: now.toISOString(),
    };
  }

  return {
    start_time: startTime ? new Date(startTime).toISOString() : undefined,
    end_time: endTime ? new Date(endTime).toISOString() : undefined,
  };
};

export const getDiffKeys = (
  oldConfig: InsightsJobConfigData,
  newConfig: InsightsJobFormData
): string[] => {
  const oldConfigObj = convertFormDataToConfig(
    convertConfigToFormData(oldConfig)
  );
  const newConfigObj = convertFormDataToConfig(newConfig);
  const diffKeys = Object.keys(oldConfigObj).reduce((acc, key) => {
    if (
      (oldConfigObj[key] == null && newConfigObj[key] == undefined) ||
      (oldConfigObj[key] == undefined && newConfigObj[key] == null) ||
      dequal(oldConfigObj[key], newConfigObj[key])
    ) {
      return acc;
    } else {
      acc.push(key);
    }
    return acc;
  }, [] as string[]);

  return diffKeys;
};
